import React, { type FC, forwardRef, type PropsWithChildren } from 'react'
import { Link, type LinkProps } from '@mui/material'

import type { AllUnionKeys } from '../../../../utils'

import type { RouteParentPath } from '../Config'
import {
    type AbsoluteRouteOnlyPath,
    type AbsoluteRoutePathWithId,
    type AbsoluteRoutePathWithServiceId,
    type AbsoluteRouteUpdateArgs,
    isRelativeRoutePathWithId,
    type RelativeRoutePathWithId,
} from '../Domain'
import { useHasCapabilities } from '../Hook.HasCapabilities'
import { useInternalRoutingHook } from '../Hook.InternalRouting'

type RoutingProps = Readonly<
    | { path: AbsoluteRoutePathWithServiceId | AbsoluteRoutePathWithId | RelativeRoutePathWithId, id: string }
    | { path: AbsoluteRouteOnlyPath }
    | { parent: RouteParentPath }
>

type CosmeticProps = Pick<LinkProps, 'color' | 'sx' | 'title' | 'aria-current' | 'onClick'>
type WrapperProps = Readonly<{ authorized: boolean }>
type WrapperAsArgsProps = Readonly<{ wrapper?: FC<PropsWithChildren<WrapperProps>> }>

type InternalHook = ReturnType<typeof useInternalRoutingHook>

type InternalProps =
    & RoutingProps
    & CosmeticProps
    & WrapperAsArgsProps
    & Readonly<{ routing: InternalHook, hasCapabilities: ReturnType<typeof useHasCapabilities> }>

const splitRoutingProps = <P extends RoutingProps>(props: P): [RoutingProps, Omit<P, AllUnionKeys<RoutingProps>>] => {
    type OutType = Omit<P, AllUnionKeys<RoutingProps>>

    if ('parent' in props) {
        const { parent, ...parentLessProps } = props
        return [{ parent }, parentLessProps as unknown as OutType]
    }

    if ('id' in props) {
        const { path, id, ...idLessProps } = props
        return [{ path, id }, idLessProps as unknown as OutType]
    }

    const { path, ...pathLessProps } = props
    return [{ path }, pathLessProps as unknown as OutType]
}

const resolveAbsoluteRouteUpdateArgs = (routing: InternalHook, props: RoutingProps): AbsoluteRouteUpdateArgs => {
    if ('id' in props) {
        const route = isRelativeRoutePathWithId(props.path) ? routing.getAbsoluteRoute(props.path) : props.path
        return [route, props.id]
    }

    if ('parent' in props) {
        return routing.getParentAbsoluteRouteUpdateArgs(props.parent)
    }

    return [props.path]
}

const defaultWrapper: FC<PropsWithChildren<WrapperProps>> = ({ children }) => <>{children}</>

const InternalLink = forwardRef<HTMLAnchorElement, PropsWithChildren<InternalProps>>((allProps, ref) => {
    const [routeProps, { wrapper: Wrapper = defaultWrapper, routing, hasCapabilities, children, ...props }] = splitRoutingProps<
        PropsWithChildren<InternalProps>
    >(allProps)

    const args = resolveAbsoluteRouteUpdateArgs(routing, routeProps)

    const [route] = args
    const authorized = hasCapabilities(route)

    return (
        <Wrapper authorized={authorized}>
            {authorized
                ? (
                    // data-testid comes before the spread so a caller's own one (via props) overrides this default.
                    <Link data-testid={`permissioned-link-${route}`} {...props} ref={ref} underline="none" href={routing.createHref(...args)}>
                        {children}
                    </Link>
                )
                : children}
        </Wrapper>
    )
})

type ExternalProps = RoutingProps & CosmeticProps & WrapperAsArgsProps

export const usePermissionedLink: () => FC<PropsWithChildren<ExternalProps>> = () => {
    const hasCapabilities = useHasCapabilities()
    const routing = useInternalRoutingHook()
    return (props) => <InternalLink {...props} routing={routing} hasCapabilities={hasCapabilities} />
}

export const PermissionedLink: FC<PropsWithChildren<ExternalProps>> = (props) => {
    const InternalLink = usePermissionedLink()
    return <InternalLink {...props} />
}

/**
 * Ref-forwarding version of PermissionedLink.
 * Required for usage inside MUI Tooltip, which expects its child
 * to accept a ref and pass it to a DOM element.
 * @see https://mui.com/material-ui/react-tooltip/#custom-child-element
 */
export const ForwardRefPermissionedLink = forwardRef<HTMLAnchorElement, PropsWithChildren<ExternalProps>>((props, ref) => {
    const hasCapabilities = useHasCapabilities()
    const routing = useInternalRoutingHook()

    return <InternalLink {...props} ref={ref} routing={routing} hasCapabilities={hasCapabilities} />
})
